/**
 * Level 3: 品种 + 情绪 (spec 2.4, plan 4.x)
 * 品种：MobileNet ImageNet 分类 → 狗类 151–268 映射中文（宪法：MobileNetV3/EfficientNet Lite）
 * 情绪：多帧 Pose 髋部中点位移 Delta → 幅度/频率映射情绪标签
 */

import * as mobilenet from '@tensorflow-models/mobilenet';
import '@tensorflow/tfjs-backend-webgl';
import {
  isDogClassIndex,
  imagenetDogIndexToZh,
} from './imagenetDogMap';
import type { PoseLandmarksFrame } from './level2';

const MOBILENET_INPUT_SIZE = 224;
const DOG_INDEX_MIN = 151;
const DOG_INDEX_MAX = 268;

let breedNet: mobilenet.MobileNet | null = null;

export interface BreedResult {
  breed: string;
  breedMeta: string;
}

const BREED_LOAD_RETRIES = 3;
const BREED_LOAD_RETRY_DELAY_MS = 1500;

function isRetryableNetworkError(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : String(e);
  return /failed to fetch|network|ERR_NETWORK|load failed/i.test(msg);
}

export async function initBreedModel(): Promise<void> {
  if (breedNet) return;
  let lastError: unknown;
  for (let attempt = 1; attempt <= BREED_LOAD_RETRIES; attempt++) {
    try {
      breedNet = await mobilenet.load({ version: 2, alpha: 1.0 });
      return;
    } catch (e) {
      lastError = e;
      console.warn(`Breed model load failed (attempt ${attempt}/${BREED_LOAD_RETRIES}):`, e);
      if (attempt < BREED_LOAD_RETRIES && isRetryableNetworkError(e)) {
        await new Promise((r) => setTimeout(r, BREED_LOAD_RETRY_DELAY_MS));
      } else {
        break;
      }
    }
  }
  console.warn('Breed model unavailable after retries:', lastError);
}

/**
 * 从全图 canvas 按 bbox 裁剪并 resize 到 224，再跑 MobileNet；取 top-1 若为狗类则映射中文
 */
export async function predictBreed(
  canvas: HTMLCanvasElement,
  bbox: { x: number; y: number; w: number; h: number }
): Promise<BreedResult> {
  if (!breedNet) {
    initBreedModel().catch(() => {}); // 按需再试一次，网络恢复后下次可能可用
    return { breed: '品种模型未加载', breedMeta: '网络波动时可稍候重试或刷新' };
  }
  try {
    const { x, y, w, h } = bbox;
    const srcW = canvas.width;
    const srcH = canvas.height;
    const pad = 0.1;
    const x1 = Math.max(0, x - w * pad);
    const y1 = Math.max(0, y - h * pad);
    const w1 = Math.min(srcW - x1, w * (1 + 2 * pad));
    const h1 = Math.min(srcH - y1, h * (1 + 2 * pad));
    const crop = document.createElement('canvas');
    crop.width = MOBILENET_INPUT_SIZE;
    crop.height = MOBILENET_INPUT_SIZE;
    const ctx = crop.getContext('2d');
    if (!ctx) return { breed: '预处理失败', breedMeta: '' };
    ctx.drawImage(
      canvas,
      x1, y1, w1, h1,
      0, 0, MOBILENET_INPUT_SIZE, MOBILENET_INPUT_SIZE
    );
    const preds = await breedNet.classify(crop, 5);
    for (const p of preds) {
      const index = indexFromClassName(p.className);
      if (index != null && isDogClassIndex(index)) {
        const zh = imagenetDogIndexToZh(index);
        const pct = Math.round((p.probability ?? 0) * 100);
        return { breed: zh, breedMeta: `相似度 ${Math.min(99, Math.max(50, pct))}%` };
      }
    }
    return { breed: '未识别品种', breedMeta: '未匹配到犬类' };
  } catch (e) {
    console.warn('Breed predict error:', e);
    return { breed: '识别失败', breedMeta: '请重试' };
  }
}

/** 通过 className 反查 ImageNet index（狗类 151–268） */
function indexFromClassName(className: string): number | null {
  const lower = className.toLowerCase().trim();
  for (let i = DOG_INDEX_MIN; i <= DOG_INDEX_MAX; i++) {
    const en = IMAGENET_DOG_EN[i];
    if (!en) continue;
    const parts = en.split(',').map((s) => s.trim().toLowerCase());
    if (parts.some((p) => p === lower || lower.includes(p) || p.includes(lower))) return i;
  }
  return null;
}

/** ImageNet 狗类 151–268 英文标签（与 imagenet_classes 一致） */
const IMAGENET_DOG_EN: Record<number, string> = {
  151: 'Chihuahua', 152: 'Japanese spaniel', 153: 'Maltese dog, Maltese terrier, Maltese',
  154: 'Pekinese, Pekingese, Peke', 155: 'Shih-Tzu', 156: 'Blenheim spaniel', 157: 'papillon',
  158: 'toy terrier', 159: 'Rhodesian ridgeback', 160: 'Afghan hound, Afghan',
  161: 'basset, basset hound', 162: 'beagle', 163: 'bloodhound, sleuthhound', 164: 'bluetick',
  165: 'black-and-tan coonhound', 166: 'Walker hound, Walker foxhound', 167: 'English foxhound',
  168: 'redbone', 169: 'borzoi, Russian wolfhound', 170: 'Irish wolfhound', 171: 'Italian greyhound',
  172: 'whippet', 173: 'Ibizan hound, Ibizan Podenco', 174: 'Norwegian elkhound, elkhound',
  175: 'otterhound, otter hound', 176: 'Saluki, gazelle hound', 177: 'Scottish deerhound, deerhound',
  178: 'Weimaraner', 179: 'Staffordshire bullterrier, Staffordshire bull terrier',
  180: 'American Staffordshire terrier, Staffordshire terrier, American pit bull terrier, pit bull terrier',
  181: 'Bedlington terrier', 182: 'Border terrier', 183: 'Kerry blue terrier', 184: 'Irish terrier',
  185: 'Norfolk terrier', 186: 'Norwich terrier', 187: 'Yorkshire terrier', 188: 'wire-haired fox terrier',
  189: 'Lakeland terrier', 190: 'Sealyham terrier, Sealyham', 191: 'Airedale, Airedale terrier',
  192: 'cairn, cairn terrier', 193: 'Australian terrier', 194: 'Dandie Dinmont, Dandie Dinmont terrier',
  195: 'Boston bull, Boston terrier', 196: 'miniature schnauzer', 197: 'giant schnauzer',
  198: 'standard schnauzer', 199: 'Scotch terrier, Scottish terrier, Scottie',
  200: 'Tibetan terrier, chrysanthemum dog', 201: 'silky terrier, Sydney silky',
  202: 'soft-coated wheaten terrier', 203: 'West Highland white terrier', 204: 'Lhasa, Lhasa apso',
  205: 'flat-coated retriever', 206: 'curly-coated retriever', 207: 'golden retriever',
  208: 'Labrador retriever', 209: 'Chesapeake Bay retriever', 210: 'German short-haired pointer',
  211: 'vizsla, Hungarian pointer', 212: 'English setter', 213: 'Irish setter, red setter',
  214: 'Gordon setter', 215: 'Brittany spaniel', 216: 'clumber, clumber spaniel',
  217: 'English springer, English springer spaniel', 218: 'Welsh springer spaniel',
  219: 'cocker spaniel, English cocker spaniel, cocker', 220: 'Sussex spaniel',
  221: 'Irish water spaniel', 222: 'kuvasz', 223: 'schipperke', 224: 'groenendael',
  225: 'malinois', 226: 'briard', 227: 'kelpie', 228: 'komondor',
  229: 'Old English sheepdog, bobtail', 230: 'Shetland sheepdog, Shetland sheep dog, Shetland',
  231: 'collie', 232: 'Border collie', 233: 'Bouvier des Flandres, Bouviers des Flandres',
  234: 'Rottweiler', 235: 'German shepherd, German shepherd dog, German police dog, alsatian',
  236: 'Doberman, Doberman pinscher', 237: 'miniature pinscher', 238: 'Greater Swiss Mountain dog',
  239: 'Bernese mountain dog', 240: 'Appenzeller', 241: 'EntleBucher', 242: 'boxer',
  243: 'bull mastiff', 244: 'Tibetan mastiff', 245: 'French bulldog', 246: 'Great Dane',
  247: 'Saint Bernard, St Bernard', 248: 'Eskimo dog, husky', 249: 'malamute, malemute, Alaskan malamute',
  250: 'Siberian husky', 251: 'dalmatian, coach dog, carriage dog', 252: 'affenpinscher, monkey pinscher, monkey dog',
  253: 'basenji', 254: 'pug, pug-dog', 255: 'Leonberg', 256: 'Newfoundland, Newfoundland dog',
  257: 'Great Pyrenees', 258: 'Samoyed, Samoyede', 259: 'Pomeranian', 260: 'chow, chow chow',
  261: 'keeshond', 262: 'Brabancon griffon', 263: 'Pembroke, Pembroke Welsh corgi',
  264: 'Cardigan, Cardigan Welsh corgi', 265: 'toy poodle', 266: 'miniature poodle', 267: 'standard poodle',
  268: 'Mexican hairless',
};

export interface EmotionResult {
  emotion: string;
  emotionMeta: string;
}

const LANDMARK_LEFT_HIP = 23;
const LANDMARK_RIGHT_HIP = 24;

/**
 * 由多帧姿态历史计算髋部中点位移（尾部近似），映射到情绪
 * plan: 尾部关键点位移 Delta 幅度/频率 → 情绪标签
 */
export function inferEmotionFromPoseHistory(
  history: PoseLandmarksFrame[] | null
): EmotionResult {
  if (!history || history.length < 2) {
    return { emotion: '放松', emotionMeta: '多看几秒更准' };
  }
  const points: { t: number; x: number; y: number }[] = [];
  for (const frame of history) {
    const lm = frame.landmarks;
    const lh = lm[LANDMARK_LEFT_HIP];
    const rh = lm[LANDMARK_RIGHT_HIP];
    if (lh && rh) {
      points.push({
        t: frame.timestamp,
        x: (lh.x + rh.x) / 2,
        y: (lh.y + rh.y) / 2,
      });
    }
  }
  if (points.length < 2) {
    return { emotion: '放松', emotionMeta: '关键点不足' };
  }
  let totalMotion = 0;
  let count = 0;
  for (let i = 1; i < points.length; i++) {
    const dt = (points[i].t - points[i - 1].t) / 1000;
    if (dt <= 0) continue;
    const dx = points[i].x - points[i - 1].x;
    const dy = points[i].y - points[i - 1].y;
    const v = Math.hypot(dx, dy) / dt;
    totalMotion += v;
    count++;
  }
  const avgMotion = count > 0 ? totalMotion / count : 0;
  if (avgMotion > 0.08) {
    return { emotion: '超开心', emotionMeta: '基于身体摆动幅度' };
  }
  if (avgMotion > 0.04) {
    return { emotion: '好奇', emotionMeta: '基于姿态变化' };
  }
  if (avgMotion > 0.015) {
    return { emotion: '放松', emotionMeta: '动作较平稳' };
  }
  return { emotion: '放松', emotionMeta: '状态很自在' };
}
